import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { useAuth } from '../lib/auth';
import { profileAPI, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { User, Mail, Phone, KeyRound, Pause, Trash2, Save, ShieldAlert } from 'lucide-react';

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  const [freezeOpen, setFreezeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === 'admin';

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await profileAPI.update({ name, email, phone });
      toast.success('Profil güncellendi');
      await refreshUser();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error('Yeni şifre en az 6 karakter olmalı'); return; }
    setSavingPw(true);
    try {
      await profileAPI.changePassword(currentPw, newPw);
      toast.success('Şifreniz güncellendi');
      setCurrentPw(''); setNewPw('');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPw(false); }
  };

  const doFreeze = async () => {
    setBusy(true);
    try {
      await profileAPI.freeze();
      toast.success('Hesabınız donduruldu');
      await logout();
      navigate('/');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); setFreezeOpen(false); }
  };

  const doDelete = async () => {
    if (!deletePw) { toast.error('Şifrenizi girin'); return; }
    setBusy(true);
    try {
      await profileAPI.delete(deletePw);
      toast.success('Hesabınız silindi');
      await logout();
      navigate('/');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1">Profilim</h1>
          <p className="text-muted-foreground text-sm">Kişisel bilgilerinizi ve hesap güvenliğinizi yönetin.</p>
        </div>

        {/* Profile info */}
        <form onSubmit={saveProfile} className="glass-card rounded-2xl p-6 mb-6" data-testid="profile-form">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><User className="w-4 h-4 text-fuchsia-400" /> Hesap Bilgileri</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="name">Ad Soyad</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="name" value={name} onChange={(e)=>setName(e.target.value)} className="pl-9 bg-white/5 border-white/10" data-testid="profile-name-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">E-posta</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="pl-9 bg-white/5 border-white/10" data-testid="profile-email-input" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="phone">Telefon <span className="text-xs text-muted-foreground">(opsiyonel)</span></Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="phone" type="tel" placeholder="+90 555 123 45 67" value={phone} onChange={(e)=>setPhone(e.target.value)} className="pl-9 bg-white/5 border-white/10" data-testid="profile-phone-input" />
              </div>
            </div>
          </div>
          <Button type="submit" disabled={savingProfile} className="bg-gradient-to-r from-fuchsia-500 to-indigo-500" data-testid="profile-save-btn">
            <Save className="w-4 h-4 mr-2" /> {savingProfile ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
          </Button>
        </form>

        {/* Password */}
        <form onSubmit={savePassword} className="glass-card rounded-2xl p-6 mb-6" data-testid="password-form">
          <h2 className="font-semibold mb-4 flex items-center gap-2"><KeyRound className="w-4 h-4 text-fuchsia-400" /> Şifre Değiştir</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="curpw">Mevcut Şifre</Label>
              <Input id="curpw" type="password" value={currentPw} onChange={(e)=>setCurrentPw(e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="profile-current-pw" required />
            </div>
            <div>
              <Label htmlFor="newpw">Yeni Şifre <span className="text-xs text-muted-foreground">(min 6)</span></Label>
              <Input id="newpw" type="password" value={newPw} onChange={(e)=>setNewPw(e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="profile-new-pw" minLength={6} required />
            </div>
          </div>
          <Button type="submit" disabled={savingPw} className="bg-gradient-to-r from-fuchsia-500 to-indigo-500" data-testid="profile-change-pw-btn">
            {savingPw ? 'Güncelleniyor...' : 'Şifreyi Değiştir'}
          </Button>
        </form>

        {/* Danger zone */}
        {!isAdmin && (
          <div className="glass-card rounded-2xl p-6 border border-rose-500/20" data-testid="danger-zone">
            <h2 className="font-semibold mb-1 flex items-center gap-2 text-rose-400"><ShieldAlert className="w-4 h-4" /> Tehlikeli Alan</h2>
            <p className="text-xs text-muted-foreground mb-4">Bu işlemler geri alınamaz veya yönetici onayı gerektirir.</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={()=>setFreezeOpen(true)} data-testid="freeze-btn">
                <Pause className="w-4 h-4 mr-2" /> Hesabı Dondur
              </Button>
              <Button variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" onClick={()=>setDeleteOpen(true)} data-testid="delete-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Hesabı Sil
              </Button>
            </div>
          </div>
        )}

        {/* Freeze dialog */}
        <Dialog open={freezeOpen} onOpenChange={setFreezeOpen}>
          <DialogContent className="bg-background border-white/10">
            <DialogHeader>
              <DialogTitle>Hesabı Dondur</DialogTitle>
              <DialogDescription>Hesabınız donar ve giriş yapamazsınız. Yeniden aktifleştirmek için yöneticiyle iletişime geçmeniz gerekir. Bakiyeniz korunur.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setFreezeOpen(false)}>Vazgeç</Button>
              <Button onClick={doFreeze} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="freeze-confirm">
                {busy ? 'İşleniyor...' : 'Evet, dondur'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete dialog */}
        <Dialog open={deleteOpen} onOpenChange={(o)=>{ setDeleteOpen(o); if(!o) setDeletePw(''); }}>
          <DialogContent className="bg-background border-white/10">
            <DialogHeader>
              <DialogTitle className="text-rose-400">Hesabı Kalıcı Olarak Sil</DialogTitle>
              <DialogDescription>Bu işlem geri alınamaz. Hesabınız ve bakiyeniz silinir, indirme geçmişiniz arşivde kalır.</DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label htmlFor="dpw">Şifrenizi onaylayın</Label>
              <Input id="dpw" type="password" value={deletePw} onChange={(e)=>setDeletePw(e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="delete-confirm-pw" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setDeleteOpen(false)}>Vazgeç</Button>
              <Button onClick={doDelete} disabled={busy} className="bg-rose-500 hover:bg-rose-600" data-testid="delete-confirm">
                {busy ? 'Siliniyor...' : 'Evet, kalıcı olarak sil'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
